// src/services/examService.ts

import { OfficialExam, UserExamAttempt, CreateExamData, ExamAnswer } from '../types/exam.types';

const API_BASE_URL = '/api';

class ExamService {
    private getHeaders(): HeadersInit {
        const token = localStorage.getItem('accessToken');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    }

    // ========== USUÁRIOS ==========

    /**
     * Listar provas publicadas
     */
    async getExams(filters?: { institution?: string; year?: number; area?: string }): Promise<OfficialExam[]> {
        const params = new URLSearchParams();
        if (filters?.institution) params.append('institution', filters.institution);
        if (filters?.year) params.append('year', filters.year.toString());
        if (filters?.area) params.append('area', filters.area);

        const response = await fetch(
            `${API_BASE_URL}/exams?${params.toString()}`,
            { headers: this.getHeaders() }
        );

        if (!response.ok) {
            throw new Error('Erro ao buscar provas');
        }

        const data = await response.json();
        return data.exams;
    }

    /**
     * Obter prova específica
     */
    async getExam(examId: string): Promise<OfficialExam> {
        const response = await fetch(
            `${API_BASE_URL}/exams/${examId}`,
            { headers: this.getHeaders() }
        );

        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            if (body.interdicted) throw Object.assign(new Error('INTERDICTED'), { interdicted: true });
            throw new Error('Erro ao buscar prova');
        }

        const data = await response.json();
        return data.exam;
    }

    /**
     * Iniciar tentativa de prova
     */
    async startExam(examId: string): Promise<UserExamAttempt> {
        const response = await fetch(
            `${API_BASE_URL}/exams/${examId}/start`,
            {
                method: 'POST',
                headers: this.getHeaders()
            }
        );

        if (!response.ok) {
            throw new Error('Erro ao iniciar prova');
        }

        const data = await response.json();
        return data.attempt;
    }

    /**
     * Finalizar tentativa de prova
     */
    async finishExam(attemptId: string, answers: ExamAnswer[]): Promise<UserExamAttempt> {
        const response = await fetch(
            `${API_BASE_URL}/exams/attempt/${attemptId}/finish`,
            {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({ answers })
            }
        );

        if (!response.ok) {
            throw new Error('Erro ao finalizar prova');
        }

        const data = await response.json();
        return data.attempt;
    }

    /**
     * Listar tentativas do usuário
     */
    async getMyAttempts(): Promise<UserExamAttempt[]> {
        const response = await fetch(
            `${API_BASE_URL}/exams/attempts/me`,
            { headers: this.getHeaders() }
        );

        if (!response.ok) {
            throw new Error('Erro ao buscar tentativas');
        }

        const data = await response.json();
        return data.attempts;
    }

    /**
     * Obter tentativa específica
     */
    async getAttempt(attemptId: string): Promise<UserExamAttempt> {
        const response = await fetch(
            `${API_BASE_URL}/exams/attempt/${attemptId}`,
            { headers: this.getHeaders() }
        );

        if (!response.ok) {
            throw new Error('Erro ao buscar tentativa');
        }

        const data = await response.json();
        return data.attempt;
    }

    // ========== ADMIN ==========

    /**
     * Listar simulados Graia publicados (user)
     */
    async getGraiaSimulados(): Promise<OfficialExam[]> {
        const response = await fetch(
            `${API_BASE_URL}/exams/graia-simulados`,
            { headers: this.getHeaders() }
        );
        if (!response.ok) throw new Error('Erro ao buscar simulados Graia');
        const data = await response.json();
        return data.simulados;
    }

    /**
     * Deletar tentativa do histórico (user)
     */
    async deleteAttempt(attemptId: string): Promise<void> {
        const response = await fetch(
            `${API_BASE_URL}/exams/attempts/${attemptId}`,
            { method: 'DELETE', headers: this.getHeaders() }
        );
        if (!response.ok) throw new Error('Erro ao deletar tentativa');
    }

    // ========== ADMIN ==========

    /**
     * Listar todas as provas (admin)
     */
    async getExamsAdmin(filters?: { status?: string; type?: string }): Promise<OfficialExam[]> {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.type) params.append('type', filters.type);

        const response = await fetch(
            `${API_BASE_URL}/admin/exams?${params.toString()}`,
            { headers: this.getHeaders() }
        );

        if (!response.ok) {
            throw new Error('Erro ao buscar provas');
        }

        const data = await response.json();
        return data.exams;
    }

    /**
     * Criar prova (admin)
     */
    async createExam(data: CreateExamData): Promise<OfficialExam> {
        const response = await fetch(
            `${API_BASE_URL}/admin/exams`,
            {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify(data)
            }
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Erro ao criar prova');
        }

        const result = await response.json();
        return result.exam;
    }

    /**
     * Atualizar prova (admin)
     */
    async updateExam(examId: string, updates: Partial<CreateExamData>): Promise<OfficialExam> {
        const response = await fetch(
            `${API_BASE_URL}/admin/exams/${examId}`,
            {
                method: 'PUT',
                headers: this.getHeaders(),
                body: JSON.stringify(updates)
            }
        );

        if (!response.ok) {
            throw new Error('Erro ao atualizar prova');
        }

        const data = await response.json();
        return data.exam;
    }

    /**
     * Interditar/desinterditar prova ou simulado (admin)
     */
    async toggleInterdict(examId: string): Promise<{ interdicted: boolean }> {
        const response = await fetch(
            `${API_BASE_URL}/admin/exams/${examId}/toggle-interdict`,
            { method: 'PATCH', headers: this.getHeaders() }
        );
        if (!response.ok) throw new Error('Erro ao interditar');
        return await response.json();
    }

    /**
     * Publicar/Despublicar prova (admin)
     */
    async togglePublish(examId: string): Promise<{ status: string }> {
        const response = await fetch(
            `${API_BASE_URL}/admin/exams/${examId}/toggle-publish`,
            {
                method: 'PATCH',
                headers: this.getHeaders()
            }
        );

        if (!response.ok) {
            throw new Error('Erro ao alterar status');
        }

        return await response.json();
    }

    /**
     * Deletar prova (admin)
     */
    async deleteExam(examId: string, deleteQuestions = false): Promise<void> {
        const response = await fetch(
            `${API_BASE_URL}/admin/exams/${examId}`,
            {
                method: 'DELETE',
                headers: { ...this.getHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ deleteQuestions: !!deleteQuestions }),
            }
        );

        if (!response.ok) {
            throw new Error('Erro ao deletar prova');
        }
    }

    /**
     * Importar prova via JSON (admin)
     */
    async importExam(data: {
        name: string;
        institution: string;
        year: number;
        area?: string;
        level?: string;
        officialDuration?: number;
        questions: any[];
        questionStatus: 'prova' | 'publicado' | 'rascunho';
    }): Promise<{ exam: OfficialExam; results: { imported: number; failed: number; errors: any[] } }> {
        const response = await fetch(
            `${API_BASE_URL}/admin/exams/import`,
            {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify(data)
            }
        );

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.message || 'Erro ao importar prova');
        }

        return await response.json();
    }

    /**
     * Buscar questões para adicionar à prova (admin) com filtros e paginação
     */
    async searchQuestionsForExam(params: {
        search?: string;
        status?: string;
        difficulty?: string;
        page?: number;
        limit?: number;
    } = {}): Promise<{ questions: any[]; pagination: any }> {
        const { questionService } = await import('./questionService');
        return questionService.getQuestionsAdmin({ ...params, limit: params.limit ?? 20 });
    }
}

export const examService = new ExamService();